import { useEffect, useState } from 'react';
import { ENV, PADDLE_CLIENT_TOKEN, PUBLIC_URL } from '../utils/const';
import Layout from '../layouts/Layout';
import { PaddlePricing } from '../components/PaddlePricing';
import { Paddle, initializePaddle } from '@paddle/paddle-js';
import { useTranslation } from 'react-i18next';
import { TestimonialCard } from '../components/TestimonialCard';
import { BlogProperties, getBlogForLanguage } from 'reactyll';
import { blogs } from '../_blog/routes';
import { ExtraProperties } from '../components/BlogTemplate';
import { Head } from 'vite-react-ssg';
import { useFathom } from '../contexts/FathomContext';
import {
    Container,
    Grid,
    Title,
    Text,
    Button,
    Stack,
    Group,
    Badge,
    Accordion,
    Box,
    Anchor,
    Divider,
    Card,
    Image,
    Paper,
    Center,
    Flex
} from '@mantine/core';
import { IconExchange, IconLockFilled, IconSpy } from '@tabler/icons-react';
import { AppleSVG, GoogleSVG, ICSLogoSVG, OutlookSVG } from '../components/SVG';
import { useLocation } from 'react-router-dom';
import { redirectWithParameters } from '../utils/common';

const FaqElement = ({ question, answer }: { question: string, answer: string }) => {
    return (
        <div key={question}>
            <Accordion.Item value={question}>
                <Accordion.Control py='xs' styles={{ label: { fontSize: 'var(--mantine-font-size-lg)', fontWeight: '500' } }}><Title order={3} fw={600}>{question}</Title></Accordion.Control>
                <Accordion.Panel><Text fs='xl' dangerouslySetInnerHTML={{ __html: answer }} /></Accordion.Panel>
            </Accordion.Item>
            <Divider color='gray.5' />
        </div>
    )
}

const InnerHome: React.FC = () => {
    const { t, i18n } = useTranslation();
    const [paddle, setPaddle] = useState<Paddle | null>(null);
    const { trackEvent } = useFathom();
    const location = useLocation();


    const signup = (cta_name: string) => {
        trackEvent(`Home > ${cta_name} Clicked`);
        redirectWithParameters(`${PUBLIC_URL}/login`, {}, true);
    }

    async function setupPaddle() {
        try {
            const paddleInstance = await initializePaddle({ environment: ENV === "production" ? "production" : "sandbox", token: PADDLE_CLIENT_TOKEN });
            if (paddleInstance) {
                setPaddle(paddleInstance);
            }
        } catch (e) {
            console.error(`Failed to initialize paddle: ${e}`)
        }
    }

    useEffect(() => {
        setupPaddle();
    }, [])

    useEffect(() => {
        // Handle initial hash navigation after component mount
        if (window.location.hash) {
            const id = window.location.hash.substring(1); // Remove the # character
            setTimeout(() => {
                const element = document.getElementById(id);
                element?.scrollIntoView({ behavior: 'smooth' });
            }, 100); // Small delay to ensure the element exists
        }
    }, []);

    const CTAButtonProps = { color: 'primary.4', radius: 'md', fw: 300, c: 'white', px: 'xl', size: "compact-xl" }

    return (
        <>
            <Head>
                <meta charSet="utf-8" />
                <title>{t('common.title_sync_calendars')}</title>
                <link rel="canonical" href={`https://calensync.live${location.pathname.replace('/en', '')}`} />
                <link rel="alternate" href={`https://calensync.live${PUBLIC_URL}/es`} hrefLang="es" />
                <link rel="alternate" href={`https://calensync.live${PUBLIC_URL}/fr`} hrefLang="fr" />
                <link rel="alternate" href={`https://calensync.live${PUBLIC_URL}/en`} hrefLang="en" />
                <link rel="alternate" href={`https://calensync.live${PUBLIC_URL}/it`} hrefLang="it" />
                <link rel="alternate" href={`https://calensync.live${PUBLIC_URL}`} hrefLang="x-default" />
                <meta name="description" content={t("home.meta.description")} />
                <meta property="og:title" content={t("home.meta.og_title")} />
                <meta property="og:url" content={`https://calensync.live${PUBLIC_URL}`} />
                <meta property="og:description" content={t("home.meta.description")} />
            </Head>

            {/* Hero Section */}
            <Box className='hero'>
                <Container size="lg" py="xl">
                    <Grid align="center" justify="center" gap="xl" py={{ base: 'sm', md: 'xl' }}>
                        <Grid.Col span={{ base: 12, sm: 6 }}>
                            <Stack>
                                <Title order={1} className='display-3'>{t("home.hero.title")}</Title>
                                <Title order={2} fw={200} dangerouslySetInnerHTML={{ __html: t("home.hero.heading") }} />
                                <Stack gap={'4'}>
                                    <Group grow={true} hiddenFrom="md">
                                        <Button {...CTAButtonProps} onClick={() => signup("hero")}>
                                            {t("home.hero.cta")}
                                        </Button>
                                    </Group>
                                    <Group grow={false} visibleFrom="md">
                                        <Button {...CTAButtonProps} onClick={() => signup("hero")}>
                                            {t("home.hero.cta")}
                                        </Button>
                                    </Group>
                                    <Text size="sm" >{t("common.free_trial")}</Text>
                                </Stack>
                            </Stack>
                        </Grid.Col>
                        <Grid.Col span={{ base: 12, sm: 6 }}>
                            <Box w="100%">
                                <video
                                    autoPlay
                                    loop
                                    muted
                                    playsInline
                                    style={{
                                        isolation: 'isolate',
                                        width: '100%',
                                        height: 'auto',
                                        border: 'none', // Remove border
                                        outline: 'none', // Remove outline
                                        borderRadius: '16px',
                                        boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)' // Add shadow
                                    }}
                                    preload="none"
                                >
                                    <source src={`${PUBLIC_URL}/calensync-hero.webm`} type="video/webm" />
                                </video>
                            </Box>
                        </Grid.Col>
                        <Grid.Col span={12} mt="xl" mb="xl">
                            <Text style={{ fontSize: 'calc(1.5rem + 1vw)' }} fw={300} ta='center' pb='lg'>Compatible with</Text>
                            <Grid>
                                <Grid.Col span={{ base: 6, sm: 3 }}>
                                    <Stack justify="center" align="center" gap='xs'>
                                        <OutlookSVG width={52} height={52} />
                                        <Group justify="center" align="center" gap='xs'>
                                            <Text size="xl" lh={1} ta='center'>Microsoft Outlook</Text>
                                        </Group>
                                    </Stack>
                                </Grid.Col>
                                <Grid.Col span={{ base: 6, sm: 3 }}>
                                    <Stack justify="center" align="center" gap='xs'>
                                        <GoogleSVG width={52} height={52} />
                                        <Text size="xl" lh={1} ta='center'>Google Calendar</Text>
                                    </Stack>
                                </Grid.Col>
                                <Grid.Col span={{ base: 6, sm: 3 }}>
                                    <Stack justify="center" align="center" gap='xs'>
                                        <AppleSVG width={52} height={52} />
                                        <Group justify="center" align="center" gap='xs'>
                                            <Text size="xl" lh={1} ta='center'>Apple Calendar</Text>
                                            <Badge color='primary.4'>New!</Badge>
                                        </Group>
                                    </Stack>
                                </Grid.Col>
                                <Grid.Col span={{ base: 6, sm: 3 }}>
                                    <Stack justify="center" align="center" gap='xs'>
                                        <ICSLogoSVG width={52} height={52} />
                                        <Group justify="center" align="center" gap='xs'>
                                            <Text size="xl" lh={1} ta='center'>iCalendar</Text>
                                        </Group>
                                    </Stack>
                                </Grid.Col>
                            </Grid>
                        </Grid.Col>
                    </Grid>
                    <Group justify="center" mt="xl">
                        <Text size="sm" ta='center'>As seen on..</Text>
                        <a rel="nofollow" href="https://www.uneed.best/tool/calensync">
                            <img loading="lazy" src="https://www.uneed.best/EMBED1A.png" alt="Uneed Embed Badge" style={{ height: '42px' }} />
                        </a>
                        <a rel="nofollow" href="https://www.producthunt.com/products/calensync?utm_source=badge-follow&utm_medium=badge&utm_souce=badge-calensync" target="_blank">
                            <img loading="lazy" src="https://api.producthunt.com/widgets/embed-image/v1/follow.svg?product_id=563930&theme=light" alt="Calensync - Synchronize&#0032;your&#0032;Google&#0032;Calendars&#0032;and&#0032;avoir&#0032;future&#0032;conflicts | Product Hunt" style={{ height: '42px' }} />
                        </a>
                        <a rel="nofollow" href="https://www.saashub.com/calensync-alternatives" id="navbar-logo">
                            <img loading="lazy" alt="SaaSHub logo" src="https://cdn-b.saashub.com/assets/logo-type-b169d0d746042929da1a09247ee08d1c34f6c6ad47e4f7b8e1b95a442a67201d.png" style={{ height: '32px' }} />
                        </a>
                        <a rel="nofollow" title="alternativeto.net logo" href="https://alternativeto.net/software/calensync/about/">
                            <Stack p={0} m={0} gap={0}>
                                <img loading="lazy" alt="AlternativeTo Logo" style={{ height: '30px' }} src="https://alternativeto.net/static/a2_m.svg" />
                                <img loading="lazy" alt="AlternativeTo Logo Text" style={{ height: '12px' }} src="https://alternativeto.net/static/logo-text.svg" />
                            </Stack>
                        </a>
                    </Group>
                </Container>
            </Box>

            <Box bg='gray.0' py="xl">
                <Container size="lg" id="features" pb="xl">
                    <Center>
                        <Text size='xl' className='display-6' fw={100} lh={1.5} c='dimmed' pb='xl' mb='xl'>{t("home.features.whats_in_it_for_you")}</Text>
                    </Center>
                    <Stack>
                        <Title order={2} className='display-4'>{t("home.features.merge_all_your_calendars")}</Title>
                        <Text size="xl" fw={200} lh={1.5} dangerouslySetInnerHTML={{ __html: t("home.features.merge_all_your_calendars_content") }} />
                    </Stack>
                </Container>

                <Container size="lg" id="features" my="xl" py="xl">
                    <Stack>
                        <Title order={2} className='display-4'>{t("home.features.keep_your_privacy_intact")}</Title>

                        <Grid gap={{ base: 'md', lg: 'xl' }} align="center">
                            <Grid.Col span={{ base: 12, lg: 8 }} order={{ base: 1, lg: 1 }}>
                                <Text size="xl" fw={200} lh={1.5} dangerouslySetInnerHTML={{ __html: t("home.features.keep_your_privacy_intact_content") }} />
                            </Grid.Col>
                            <Grid.Col span={{ base: 12, lg: 4 }} order={{ base: 2, lg: 2 }}>
                                <Box mx="auto" maw="450px">
                                    <Image loading='lazy' src={`${PUBLIC_URL}/assets/personal-to-professiona-busy.png`} alt="Hide your personal calendar event details in your professional calendar" w="100%" />
                                </Box>
                            </Grid.Col>
                        </Grid>

                    </Stack>
                </Container>

                <Container size="lg" id="features" my="xl" py="xl">
                    <Stack>
                        <Title order={2} className='display-4'>{t("home.features.complete_customization")}</Title>

                        <Text size="xl" fw={200} lh={1.5} dangerouslySetInnerHTML={{ __html: t("home.features.complete_customization_content") }} />
                        <Stack gap='4'>
                            <Group>
                                <Text>✅</Text>
                                <Text fw={200} size='xl' lh={'1'}>{t("home.features.customization_features.1")}</Text>
                            </Group>
                            <Group>
                                <Text>✅</Text>
                                <Text fw={200} size='xl' lh={'1'}>{t("home.features.customization_features.2")}</Text>
                            </Group>
                            <Group>
                                <Text>✅</Text>
                                <Text fw={200} size='xl' lh={'1'}>{t("home.features.customization_features.3")}</Text>
                            </Group>
                            <Group>
                                <Text>✅</Text>
                                <Text fw={200} size='xl' lh={'1'}>{t("home.features.customization_features.4")}</Text>
                            </Group>
                            <Group>
                                <Text>✅</Text>
                                <Text fw={200} size='xl' lh={'1'}>{t("home.features.customization_features.5")}</Text>
                            </Group>
                        </Stack>

                    </Stack>
                </Container>
            </Box >

            {/* Scheduling Section */}
            <Box bg='gray.0' py="xl">
                <Container size="lg" id="features" my="xl">
                    <Center my="xl">
                        <Title order={2} className='display-4'>{t("home.features.schedule_meetings")}</Title>
                    </Center>
                    <Grid gap={{ base: 'md', lg: 'xl' }} align="center">
                        <Grid.Col span={{ base: 12, lg: 5 }} order={{ base: 1, lg: 2 }}>
                            <Text size="xl" fw={200} lh={1.5} dangerouslySetInnerHTML={{ __html: t("home.features.schedule_meetings_content") }} />
                        </Grid.Col>
                        <Grid.Col span={{ base: 12, lg: 7 }} order={{ base: 1, lg: 2 }}>
                            <Box mx="auto" maw="600px">
                                <Image loading="lazy" src={`${PUBLIC_URL}/assets/scheduling.png`} alt="Calensync Event Scheduling Page" w="100%" />
                            </Box>
                        </Grid.Col>
                    </Grid>
                </Container>
            </Box>

            {/* Pricing Section */}
            <Box className="hero" py="xl" id="pricing">
                <PaddlePricing
                    paddle={paddle}
                    isHome={true}
                    size="lg"
                    onStartTrial={() => signup("pricing")}
                />
            </Box>

            {/* Testimonials Section */}
            < Box bg='gray.0' py="xl" >
                <Container size="lg" my="xl">
                    <Stack align="center" mb={{ base: 'lg', md: 'xl' }} py={{ base: 'sm', md: 'xl' }}>
                        <Title order={2} className='display-4'>{t("testimonials.title")}</Title>
                    </Stack>
                    <Grid>
                        <Grid.Col span={{ base: 12, lg: 6 }} order={{ base: 1, lg: 2 }}>
                            {[0, 1, 2].map((idx) => (
                                <TestimonialCard key={idx} t={t} idx={idx} />
                            ))}
                        </Grid.Col>
                        <Grid.Col span={{ base: 12, lg: 6 }} order={{ base: 2, lg: 1 }}>
                            {[3, 4, 5].map((idx) => (
                                <TestimonialCard key={idx} t={t} idx={idx} />
                            ))}
                        </Grid.Col>
                    </Grid>
                </Container>
            </Box >

            {/* FAQ Section */}
            < Box bg="primary.0" py="xl" >
                <Container size="lg" py="md">
                    <Stack align="center" mb={{ base: 'lg', md: 'xl' }} py={{ base: 'sm', md: 'xl' }}>
                        <Title order={2} className='display-4'>F.A.Q.</Title>
                    </Stack>
                    <Accordion variant="contained" color='primary.4'>
                        {/* Convert each Accordion.Item to Mantine's Accordion.Item */}
                        {['00', '1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12'].map((idx) => (
                            <FaqElement key={idx} question={t(`home.faq.q${idx}`)} answer={t(`home.faq.a${idx}`)} />
                        ))}
                        <FaqElement key={13} question={t(`home.faq.q13`)} answer={t(`home.faq.a13`, { post: `${PUBLIC_URL}${getBlogForLanguage(blogs['sync-outlook-to-google'], i18n.language, "en").url}` })} />
                    </Accordion>
                </Container>
            </Box >

            {/* Blog Section */}
            < Box bg='gray.0' py="xl" >

                <Container size="lg" my="xl" py="sm">
                    <Stack align="center" mb={{ base: 'lg', md: 'xl' }} py={{ base: 'sm', md: 'xl' }}>
                        <Title order={2} className='display-4'>Blog</Title>
                    </Stack>
                    {/* Map through blog posts */}
                    {[
                        "apple-calendar-synchronization",
                        "add-buffer-time-to-event-synchronization",
                        "best-tips-for-business-calendars",
                        "efforless-meeting-scheduling",
                        "choose-the-right-freelance-calendar-tools",
                        "synchronize-all-into-one"
                    ].map((slug) => {
                        const blog = getBlogForLanguage(blogs[slug], i18n.language, "en") as BlogProperties & ExtraProperties;
                        return (
                            <Card className='blog-card' withBorder key={slug} component="a" href={`${blog.url}`} mb="md">
                                <Text size="sm" c="dimmed">Blog</Text>
                                <Title order={3} fw={500}>{blog.title}</Title>
                                <Text c="dimmed" size="lg">{blog.headline}</Text>
                            </Card>
                        );
                    })}
                    <Group justify="center">
                        <Anchor href={`${PUBLIC_URL}/blog`}>see more..</Anchor>
                    </Group>
                </Container>
            </Box >

            {/* Privacy CTA */}
            < Box bg='primary.0' mb="md" py="xl" >
                <Container py="lg" size="lg">
                    <Stack>
                        <Grid justify="center" gap="xl">
                            <Grid.Col span={{ base: 9, lg: 10 }}>
                                <Stack justify="center" align="center" pb={{ base: 'xs', md: 'xl' }} mb={{ base: 'xs', md: 'xl' }}>
                                    <Title order={2} className='display-4'>{t("home.privacy.title")}</Title>
                                    <Text ta='center' size="lg">{t("home.privacy.content")}</Text>
                                </Stack>
                            </Grid.Col>

                            <Grid.Col span={{ base: 9, lg: 4 }}>
                                <Stack>
                                    <Group justify="center"><IconExchange />{<Title order={3} fw={500} fs='lg'>{t("home.privacy.oauth.title")}</Title>}</Group>
                                    <Text size="md" ta="center">{t("home.privacy.oauth.content")}</Text>
                                </Stack>
                            </Grid.Col>
                            <Grid.Col span={{ base: 9, lg: 4 }}>
                                <Stack>
                                    <Group justify="center"><IconLockFilled />{<Title order={3} fw={500} fs='lg'>{t("home.privacy.encryption.title")}</Title>}</Group>
                                    <Text size="md" ta="center">{t("home.privacy.encryption.content")}</Text>
                                </Stack>
                            </Grid.Col>
                            <Grid.Col span={{ base: 9, lg: 4 }}>
                                <Stack>
                                    <Group justify="center"><IconSpy />{<Title order={3} fw={500} fs='lg'>{t("home.privacy.reselling.title")}</Title>}</Group>
                                    <Text size="md" ta="center">{t("home.privacy.reselling.content")}</Text>
                                </Stack>
                            </Grid.Col>
                        </Grid>

                        <Stack align="center" my='xl'>
                            <Text size="lg">{t("common.free_trial")}</Text>
                            <Button {...CTAButtonProps} onClick={() => signup("Final")}>
                                {t("home.hero.cta")}
                            </Button>
                        </Stack>
                    </Stack>

                </Container>
            </Box >
        </>
    );
};

const Home: React.FC = () => {
    return (
        <Layout onlyRequired={false}>
            <InnerHome />
        </Layout>
    );
}

export default Home;
